import PropTypes from 'prop-types';

import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';

import TextField from 'src/components/ui/TextField';
import Autocomplete from 'src/components/ui/Autocomplete';
import { useState, useLayoutEffect } from 'react';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

function parseISOToFilter(iso) {
	if (!iso) return {};

	if (typeof iso === 'object') return { value: iso.text, unit: iso.unit };

	// недели отдельно (ISO 8601: PnW)
	const weekMatch = iso.match(/^P(\d+)W$/);

	if (weekMatch) {
		return { value: Number(weekMatch[1]), unit: 'weeks' };
	}

	const d = dayjs.duration(iso);

	const days = d.asDays();
	const hours = d.asHours();
	const minutes = d.asMinutes();
	const seconds = d.asSeconds();

	if (Number.isInteger(days)) {
		return { value: days, unit: 'days' };
	}

	if (Number.isInteger(hours)) {
		return { value: hours, unit: 'hours' };
	}

	if (Number.isInteger(minutes)) {
		return { value: minutes, unit: 'minutes' };
	}

	return { value: seconds, unit: 'seconds' };
}

const resolveUnitOption = (unitValue, autocomplete) => {
	if (!unitValue || !autocomplete?.options) return null;

	return (
		autocomplete.options.find((option) => option.value === unitValue) ??
		null
	);
};

const getInitialText = (value) => {
	const parsed = parseISOToFilter(value);
	const textValue = parsed.value ?? '';

	return textValue === '' ? '' : String(textValue);
};

const getInitialUnit = (value, autocomplete) => {
	const parsed = parseISOToFilter(value);
	const fromParsed = resolveUnitOption(parsed.unit, autocomplete);

	if (fromParsed) return fromParsed;

	return autocomplete?.defaultValue ?? autocomplete?.options?.[0] ?? null;
};

const TypedField = (props) => {
	const {
		label,
		error,
		textError,
		selectError,
		helperText,
		autocomplete,
		AutocompleteRP = Autocomplete,
		onChangeValue,
		value,
		textfield,
		TextFieldRP = TextField,
		typeWidth = 164,
	} = props;

	const [text, setText] = useState(() => getInitialText(value));
	const [unit, setUnit] = useState(() => getInitialUnit(value, autocomplete));

	useLayoutEffect(() => {
		if (onChangeValue) {
			onChangeValue(null, {
				text,
				unit: unit?.value,
			});
		}
	}, [onChangeValue, text, unit]);

	return (
		<Stack spacing={1} sx={{ width: '100%' }}>
			{label && <InputLabel>{label}</InputLabel>}
			<FormControl>
				<Grid container spacing={2}>
					<Grid item xs>
						<TextFieldRP
							{...(textError ? { error: textError } : {})}
							{...textfield}
							value={text}
							onChange={(e) => setText(e.target.value)}
						/>
					</Grid>
					<Grid item width={typeWidth}>
						{autocomplete && (
							<AutocompleteRP
								{...(selectError ? { error: selectError } : {})}
								{...autocomplete}
								value={unit}
								onChange={(_, newValue) => setUnit(newValue)}
							/>
						)}
					</Grid>
				</Grid>
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
				{error && <FormHelperText error>{error}</FormHelperText>}
			</FormControl>
		</Stack>
	);
};

TypedField.propTypes = {
	typeWidth: PropTypes.number,
	label: PropTypes.string,
	error: PropTypes.string,
	textError: PropTypes.string,
	selectError: PropTypes.string,
	helperText: PropTypes.string,
	textfield: PropTypes.shape(TextField.propTypes),
	autocomplete: PropTypes.shape(Autocomplete.propTypes),
	TextFieldRP: PropTypes.elementType,
	AutocompleteRP: PropTypes.elementType,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			unit: PropTypes.string,
		}),
	]),
	onChangeValue: PropTypes.func,
};

export default TypedField;
